/**
 * "Learn with a sample" (W2-14 onboarding-wizard; DESIGN.md §3.1 "Learn with a sample -> Editor (sample project) +
 * tour"): builds the sample (sampleProject.ts) in the user's language, stores it as a project (or reopens the one
 * the user already has, when they choose so), opens it in the Chart workspace and starts the tour.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import type { ProjectId } from '../../core/model/types';
import type { I18n } from '../../i18n/types';
import { m } from './onboarding.messages';
import { buildSampleProject, type SampleProject, type SampleText } from './sampleProject';

/** The sample's texts in the current language. */
export function sampleTextOf(i18n: Pick<I18n, 't'>): SampleText {
  return {
    title: i18n.t(m.sampleTitle),
    intro: i18n.t(m.sampleIntro),
    credit: i18n.t(m.sampleCredit),
    lines: {
      main: i18n.t(m.sampleLineMain),
      partner: i18n.t(m.sampleLinePartner),
      title: i18n.t(m.sampleLineTitle),
      picture: i18n.t(m.sampleLinePicture),
      spinner: i18n.t(m.sampleLineSpinner),
      brush: i18n.t(m.sampleLineBrush),
      score: i18n.t(m.sampleLineScore),
    },
    words: [i18n.t(m.sampleWordsFirst), i18n.t(m.sampleWordsSecond)],
  };
}

type SampleServices = Pick<AppServices, 'i18n' | 'ui' | 'persist' | 'nav'>;

/** The user's existing (not trashed) sample project: the one whose song is the sample's song. */
async function existingSample(services: SampleServices, sample: SampleProject): Promise<ProjectId | null> {
  const music = sample.doc.meta.music;
  if (music === null) return null;
  try {
    const list = await services.persist.listProjects(false);
    return list.find((p) => p.musicAsset === music && p.trashedAt === null)?.id ?? null;
  } catch {
    return null;
  }
}

/** Builds (or reopens) the sample, opens it and starts the tour. Returns the project id, or null when cancelled. */
export async function openSampleProject(
  services: SampleServices,
  opts: { readonly tour?: boolean } = {},
): Promise<ProjectId | null> {
  services.ui.toast({ key: m.samplePreparing }, { kind: 'info', durationMs: 2500 });
  let sample: SampleProject;
  try {
    sample = await buildSampleProject(sampleTextOf(services.i18n));
  } catch (e) {
    services.ui.toast(
      { key: m.sampleFailed, params: { detail: e instanceof Error ? e.message : String(e) } },
      { kind: 'error' },
    );
    return null;
  }
  let id = await existingSample(services, sample);
  if (id !== null) {
    const pick = await services.ui.choose<'open' | 'fresh'>({
      title: { key: m.sampleExistsTitle },
      body: { key: m.sampleExistsBody },
      options: [
        { value: 'open', label: { key: m.sampleOpenExisting }, description: { key: m.sampleOpenExistingBody } },
        { value: 'fresh', label: { key: m.sampleFresh }, description: { key: m.sampleFreshBody } },
      ],
    });
    if (pick === null) return null;
    if (pick === 'fresh') id = null;
  }
  if (id === null) {
    try {
      id = await services.persist.createProject(
        sample.doc,
        sample.assets.map((a) => ({ id: a.id, bytes: a.bytes, mime: a.mime, role: a.role, meta: a.meta })),
      );
    } catch (e) {
      services.ui.toast(
        { key: m.sampleFailed, params: { detail: e instanceof Error ? e.message : String(e) } },
        { kind: 'error' },
      );
      return null;
    }
  }
  await services.nav.openProject(id, 'chart');
  if (opts.tour !== false) void services.ui.openDialog(DIALOG.tour, {});
  return id;
}
